import { useAuth0 } from '@auth0/auth0-react';
import axios from 'axios';
import * as LucideIcons from 'lucide-react';
import React, { useState, useEffect } from 'react';
import { Col, Row } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { convertDateIntoLocalDate } from '../../common/Functions';
import { showToast } from '../../common/ToastHandler';
import TagsHandler from '../../custom/TagsHandler';
import Assesment from '../../responseList/partials/Assesment';
import ButtonList from '../ButtonList';
import './DetailsGridBlock.css';
import './styles.css';

// Type definitions
interface IconConfig {
  type: 'lucide' | 'url' | 'image';
  value: string;
  size?: 'small' | 'medium' | 'large';
  color?: string;
  crop?: 'circle' | 'none';
}

interface StatusHandler {
  status_code: number;
  message: string;
  redirect_url?: string;
  show_toast: boolean;
  toast_type: 'success' | 'error' | 'warning' | 'info';
}

interface ResponseConfig {
  status_handlers?: StatusHandler[];
  default_handler?: StatusHandler;
  refresh_page?: boolean;
  reload_data?: boolean;
  clear_form?: boolean;
}

interface ActionConfig {
  type: 'api_call';
  url: string;
  method: string;
  body?: string;
  headers?: string;
  modal_title?: string;
  input_placeholder?: string;
  confirm_label?: string;
  cancel_label?: string;
  response?: ResponseConfig;
  default_success_message?: string;
  refresh_targets?: string[];
}

interface IconWithAction extends IconConfig {
  action?: ActionConfig;
}

interface LabelValueContent {
  kind: 'label_value';
  label?: string;
  value?: string | number;
  is_link?: boolean | null;
}

interface IconTextContent {
  kind: 'icon_text';
  icon: IconConfig;
  content: string | number;
}

interface DividerContent {
  kind: 'divider';
  orientation?: 'horizontal' | 'vertical';
  color?: string;
  thickness?: number;
}

interface SpacerContent {
  kind: 'spacer';
  size?: { default: number };
  height?: number;
}

interface CustomComponentContent {
  kind: 'custom_component';
  component_name: string;
  data: Record<string, any>;
}

type CellContent =
  | LabelValueContent
  | IconTextContent
  | DividerContent
  | SpacerContent
  | CustomComponentContent
  | {
      kind?: string;
      label?: string;
      value?: string | number;
      content?: string | number;
    };

interface Cell {
  col_span?: number;
  content?: CellContent;
  icon?: IconWithAction;
}

interface GridRow {
  cells: Cell[];
}

interface DetailsGridBlockProps {
  block: {
    kind: 'details_grid';
    columns?: number;
    gap?: string;
    rows: GridRow[];
  };
}

// Condensed grid for title/value pairs and small icon+text items
// Expects block: { kind: 'details_grid', columns, gap, rows: [{ cells: [{ col_span, content }] }] }

const toPascal = (name: string): string =>
  (name || '')
    .split(/[-_\s]/)
    .map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join('');

// Check if a value represents a boolean
const isBooleanValue = (value: string | number): boolean => {
  const str = String(value).trim().toLowerCase();
  return (
    str === 'yes' ||
    str === 'no' ||
    str === 'true' ||
    str === 'false' ||
    str === '1' ||
    str === '0' ||
    str === 'on' ||
    str === 'off'
  );
};

// Convert a boolean value to its opposite
const toggleBooleanValue = (value: string | number): string => {
  const str = String(value).trim().toLowerCase();
  if (str === 'yes' || str === 'true' || str === '1' || str === 'on') {
    // Find corresponding "No" format
    if (str === 'yes') return 'No';
    if (str === 'true') return 'False';
    if (str === '1') return '0';
    if (str === 'on') return 'Off';
    return 'false';
  } else if (str === 'no' || str === 'false' || str === '0' || str === 'off') {
    // Find corresponding "Yes" format
    if (str === 'no') return 'Yes';
    if (str === 'false') return 'True';
    if (str === '0') return '1';
    if (str === 'off') return 'On';
    return 'true';
  }
  return str; // Fallback
};

// Check if current value is "on" (true-like)
const isBooleanOn = (value: string | number): boolean => {
  const str = String(value).trim().toLowerCase();
  return str === 'yes' || str === 'true' || str === '1' || str === 'on';
};

const renderIcon = (icon: IconConfig): JSX.Element | null => {
  if (!icon) return null;
  if (icon.type === 'lucide') {
    const IconComp = LucideIcons[
      toPascal(icon.value) as keyof typeof LucideIcons
    ] as React.ComponentType<any>;
    if (!IconComp) return null;
    const sizeMap: Record<string, number> = {
      small: 16,
      medium: 20,
      large: 28,
    };
    return (
      <IconComp
        size={sizeMap[(icon.size || 'medium').toLowerCase()] || 20}
        color={icon.color || 'currentColor'}
        strokeWidth={1.75}
        className="dgb-icon"
      />
    );
  }
  if (icon.type === 'url' || icon.type === 'image') {
    const sizeMap: Record<string, number> = {
      small: 16,
      medium: 20,
      large: 28,
    };
    const px = sizeMap[(icon.size || 'medium').toLowerCase()] || 20;
    return (
      <img
        src={icon.value}
        alt=""
        className="dgb-img"
        style={{
          width: px,
          height: px,
          borderRadius: icon.crop === 'circle' ? '999px' : 0,
        }}
      />
    );
  }
  return null;
};

interface LabelValueProps {
  label?: string;
  value: string | number;
  icon?: IconWithAction;
  isLink?: boolean | null;
}

const LabelValue: React.FC<LabelValueProps> = ({
  label,
  value,
  icon,
  isLink,
}) => {
  const { getIdTokenClaims } = useAuth0();
  const navigate = useNavigate();
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [editValue, setEditValue] = useState<string>(String(value ?? ''));
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<boolean>(false);
  // Local state for toggle value to enable optimistic updates
  const [localToggleValue, setLocalToggleValue] = useState<
    string | number | undefined
  >(value);

  // Sync local state with prop value when it changes from parent
  useEffect(() => {
    setLocalToggleValue(value);
  }, [value]);

  const text = String(value ?? '');
  const shouldWrap = text.length > 15; // wrap when content is longer than 15 chars

  // Determine if this should be treated as a link
  // If isLink is explicitly set (true or false), use that value
  // Otherwise, fall back to auto-detection
  const autoDetectHttpLike =
    text.startsWith('https://') || text.startsWith('http://');
  const autoDetectRelativeUrl = text.startsWith('/') && text.includes('/'); // Relative URLs like /path/to/page

  const isHttpLike =
    isLink === false ? false : isLink === true ? true : autoDetectHttpLike;
  const isRelativeUrl =
    isLink === false ? false : isLink === true ? false : autoDetectRelativeUrl;

  const hasEditAction = icon && icon.action && icon.action.type === 'api_call';
  const isDeleteAction =
    hasEditAction &&
    icon.value === 'trash' &&
    icon.action?.method?.toUpperCase() === 'DELETE';

  // Check if this should be a toggle instead of edit
  const shouldBeToggle =
    hasEditAction && !isDeleteAction && isBooleanValue(value);
  // Use local state for toggle if available, otherwise use prop value
  const currentToggleValue =
    localToggleValue !== undefined ? localToggleValue : value;
  const isToggledOn = shouldBeToggle ? isBooleanOn(currentToggleValue) : false;

  const handleEditClick = (e: React.MouseEvent<HTMLButtonElement>): void => {
    e.preventDefault();
    e.stopPropagation();
    if (isDeleteAction) {
      setShowDeleteConfirm(true);
    } else if (hasEditAction) {
      setIsEditing(true);
      setEditValue(text);
    }
  };

  const handleSave = async (): Promise<void> => {
    if (!hasEditAction || editValue === text) {
      setIsEditing(false);
      return;
    }

    setIsLoading(true);
    try {
      const tokenClaims = await getIdTokenClaims();
      const token = tokenClaims?.__raw;

      // Parse the body template and replace placeholders properly
      let requestBodyTemplate = icon.action!.body || '{}';
      let requestBodyData: any;

      try {
        // First parse the template as JSON to get the structure
        const bodyTemplate = JSON.parse(requestBodyTemplate);

        // Create a new object with replaced values
        requestBodyData = JSON.parse(
          JSON.stringify(bodyTemplate, (key: string, value: any) => {
            if (typeof value === 'string' && value.match(/^\{[^}]+\}$/)) {
              // Replace placeholder with actual value
              return editValue;
            }
            return value;
          })
        );
      } catch (e) {
        // Fallback: simple string replacement (less safe but handles edge cases)
        requestBodyTemplate = requestBodyTemplate.replace(
          /\{[^}]+\}/g,
          JSON.stringify(editValue)
        );
        requestBodyData = JSON.parse(requestBodyTemplate);
      }

      const headers: Record<string, string> = {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
        ...(icon.action!.headers ? JSON.parse(icon.action!.headers) : {}),
      };

      const response = await axios({
        method: icon.action!.method || 'POST',
        url: icon.action!.url,
        data: requestBodyData,
        headers,
      });

      // Handle response based on status handlers
      const statusHandlers = icon.action!.response?.status_handlers || [];
      const defaultHandler = icon.action!.response?.default_handler;

      let handler =
        statusHandlers.find(h => h.status_code === response.status) ||
        defaultHandler;

      // Always show success toast with green background and white text
      if (handler && handler.show_toast) {
        showToast('success', handler.message || 'Updated successfully');
      } else {
        // Default success message if no handler specified
        showToast('success', 'Updated successfully');
      }

      setIsEditing(false);
    } catch (error) {
      console.error('Error saving edit:', error);
      const errorHandler =
        icon.action!.response?.status_handlers?.find(
          h => h.status_code === 500
        ) || icon.action!.response?.default_handler;

      if (errorHandler && errorHandler.show_toast) {
        showToast('error', errorHandler.message || 'An error occurred');
      } else {
        showToast('error', 'Failed to save changes');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = (): void => {
    setIsEditing(false);
    setEditValue(text);
  };

  const handleToggle = async (): Promise<void> => {
    if (!hasEditAction || !shouldBeToggle) return;

    // Optimistically update the toggle state immediately
    const newValue = toggleBooleanValue(currentToggleValue);
    setLocalToggleValue(newValue);
    setIsLoading(true);

    try {
      const tokenClaims = await getIdTokenClaims();
      const token = tokenClaims?.__raw;

      // Parse the body template and replace placeholders properly
      let requestBodyTemplate = icon.action!.body || '{}';
      let requestBodyData: any;

      try {
        // First parse the template as JSON to get the structure
        const bodyTemplate = JSON.parse(requestBodyTemplate);

        // Create a new object with replaced values
        requestBodyData = JSON.parse(
          JSON.stringify(bodyTemplate, (key: string, value: any) => {
            // Match placeholders like {confidential} or {placeholder}
            if (typeof value === 'string' && value.match(/\{[^}]+\}/)) {
              // Replace placeholder with toggled value
              return newValue;
            }
            return value;
          })
        );
      } catch (e) {
        // Fallback: simple string replacement (less safe but handles edge cases)
        requestBodyTemplate = requestBodyTemplate.replace(
          /\{[^}]+\}/g,
          match => {
            // Replace the placeholder with the new value, but preserve JSON structure
            // If the match is inside quotes, we need to replace it as a string value
            return JSON.stringify(newValue).slice(1, -1); // Remove outer quotes to insert the value
          }
        );
        requestBodyData = JSON.parse(requestBodyTemplate);
      }

      const headers: Record<string, string> = {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
        ...(icon.action!.headers ? JSON.parse(icon.action!.headers) : {}),
      };

      const response = await axios({
        method: icon.action!.method || 'POST',
        url: icon.action!.url,
        data: requestBodyData,
        headers,
      });

      // Handle response based on status handlers
      const statusHandlers = icon.action!.response?.status_handlers || [];
      const defaultHandler = icon.action!.response?.default_handler;

      let handler =
        statusHandlers.find(h => h.status_code === response.status) ||
        defaultHandler;

      // Show toast based on handler
      if (handler && handler.show_toast) {
        const toastType =
          handler.toast_type || (response.status === 200 ? 'success' : 'error');
        showToast(toastType, handler.message || 'Updated successfully');
      } else if (response.status === 200) {
        showToast(
          'success',
          icon.action!.default_success_message || 'Updated successfully'
        );
      }

      // Note: We're doing optimistic updates, so the toggle is already updated
      // If refresh_page is needed, let the parent handle it or use reload_data
    } catch (error: any) {
      console.error('Error toggling value:', error);
      const statusCode = error.response?.status || 500;
      const errorHandler =
        icon.action!.response?.status_handlers?.find(
          h => h.status_code === statusCode
        ) || icon.action!.response?.default_handler;

      if (errorHandler && errorHandler.show_toast) {
        const toastType = errorHandler.toast_type || 'error';
        showToast(toastType, errorHandler.message || 'An error occurred');
      } else {
        showToast('error', 'Failed to update');
      }
      // Revert optimistic update on error
      setLocalToggleValue(value);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (): Promise<void> => {
    if (!isDeleteAction) return;

    setIsLoading(true);
    try {
      const tokenClaims = await getIdTokenClaims();
      const token = tokenClaims?.__raw;

      const headers: Record<string, string> = {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
        ...(icon.action!.headers ? JSON.parse(icon.action!.headers) : {}),
      };

      const response = await axios({
        method: icon.action!.method || 'DELETE',
        url: icon.action!.url,
        headers,
      });

      // Handle response based on status handlers
      const statusHandlers = icon.action!.response?.status_handlers || [];
      const defaultHandler = icon.action!.response?.default_handler;

      let handler =
        statusHandlers.find(h => h.status_code === response.status) ||
        defaultHandler;

      if (handler && handler.show_toast) {
        showToast('success', handler.message || 'Deleted successfully');
      } else {
        showToast('success', 'Deleted successfully');
      }

      // Handle redirect - always redirect to KS list after delete success
      if (handler && handler.redirect_url) {
        window.location.href = handler.redirect_url;
      } else {
        // Always redirect to KS list page after successful deletion
        window.location.href = '/knowledge-sources';
      }

      setShowDeleteConfirm(false);
    } catch (error) {
      console.error('Error deleting:', error);
      const errorHandler =
        icon.action!.response?.status_handlers?.find(
          h => h.status_code === 500
        ) || icon.action!.response?.default_handler;

      if (errorHandler && errorHandler.show_toast) {
        showToast('error', errorHandler.message || 'An error occurred');
      } else {
        showToast('error', 'Failed to delete');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteCancel = (): void => {
    setShowDeleteConfirm(false);
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'Enter') {
      handleSave();
    } else if (e.key === 'Escape') {
      handleCancel();
    }
  };

  const renderActionIcon = (): JSX.Element | null => {
    if (!hasEditAction || !icon) return null;

    const IconComp = LucideIcons[
      toPascal(icon.value) as keyof typeof LucideIcons
    ] as React.ComponentType<any>;
    if (!IconComp) return null;

    const sizeMap: Record<string, number> = {
      small: 16,
      medium: 20,
      large: 28,
    };
    const size = sizeMap[(icon.size || 'medium').toLowerCase()] || 20;

    const buttonClass = isDeleteAction ? 'dgb-delete-icon' : 'dgb-edit-icon';
    const title = isDeleteAction ? 'Click to delete' : 'Click to edit';

    return (
      <button
        className={buttonClass}
        onClick={handleEditClick}
        title={title}
        disabled={isLoading}
      >
        <IconComp
          size={size}
          color={icon.color || (isDeleteAction ? '#ef4444' : 'currentColor')}
          strokeWidth={1.75}
        />
      </button>
    );
  };

  return (
    <div className="dgb-labelvalue">
      {label ? <div className="dgb-label">{label}</div> : null}

      {isEditing ? (
        <div className="dgb-edit-container">
          <input
            type="text"
            value={editValue}
            onChange={e => setEditValue(e.target.value)}
            onKeyDown={handleKeyPress}
            className="dgb-edit-input"
            autoFocus
            disabled={isLoading}
          />
          <div className="dgb-edit-actions">
            <button
              className="dgb-edit-save"
              onClick={handleSave}
              disabled={isLoading}
              title="Save"
            >
              {isLoading ? (
                <LucideIcons.Loader2 size={14} className="animate-spin" />
              ) : (
                <LucideIcons.Check size={14} />
              )}
            </button>
            <button
              className="dgb-edit-cancel"
              onClick={handleCancel}
              disabled={isLoading}
              title="Cancel"
            >
              <LucideIcons.X size={14} />
            </button>
          </div>
        </div>
      ) : isHttpLike ? (
        (() => {
          let lastSegment = '';
          let pathOnly = '/';
          try {
            const parsed = new URL(text, window.location.origin);
            const segments = (parsed.pathname || '/')
              .split('/')
              .filter(Boolean);
            lastSegment = segments[segments.length - 1] || '';
            pathOnly =
              (parsed.pathname || '/') +
              (parsed.search || '') +
              (parsed.hash || '');
          } catch (e) {
            // Fallback: strip origin manually and compute last segment
            const stripped = text.replace(/^https?:\/\/[^/]+/i, '');
            pathOnly = stripped.startsWith('/') ? stripped : `/${stripped}`;
            const parts = pathOnly
              .split('?')[0]
              .split('#')[0]
              .split('/')
              .filter(Boolean);
            lastSegment = parts[parts.length - 1] || '';
          }
          return (
            <div className="dgb-value-with-edit">
              <a
                href={pathOnly}
                target="_blank"
                rel="noopener noreferrer"
                className={`dgb-link ${shouldWrap ? 'dgb-wrap' : 'dgb-nowrap'}`}
              >
                {lastSegment || label || 'link'}
              </a>
              {hasEditAction && renderActionIcon()}
            </div>
          );
        })()
      ) : isRelativeUrl ? (
        <div className="dgb-value-with-edit">
          <a
            href="#"
            onClick={e => {
              e.preventDefault();
              navigate(text);
            }}
            className={`dgb-link ${shouldWrap ? 'dgb-wrap' : 'dgb-nowrap'}`}
            style={{ cursor: 'pointer' }}
          >
            {text.split('/').filter(Boolean).pop() || label || 'link'}
          </a>
          {hasEditAction && renderActionIcon()}
        </div>
      ) : shouldBeToggle ? (
        <div className="dgb-value-with-toggle">
          <label className="dgb-toggle-switch">
            <input
              type="checkbox"
              checked={isToggledOn}
              onChange={handleToggle}
              disabled={isLoading}
            />
            <span className="dgb-toggle-slider"></span>
          </label>
          {isLoading && (
            <LucideIcons.Loader2
              size={14}
              className="dgb-toggle-loader animate-spin"
            />
          )}
        </div>
      ) : (
        <div className="dgb-value-with-edit">
          <div
            className={`dgb-value ${shouldWrap ? 'dgb-wrap' : 'dgb-nowrap'}`}
          >
            {text}
          </div>
          {hasEditAction && renderActionIcon()}
        </div>
      )}

      {/* Delete Confirmation Popup */}
      {showDeleteConfirm && (
        <div className="dgb-delete-overlay">
          <div className="dgb-delete-modal">
            <div className="dgb-delete-header">
              <h3>Confirm Delete</h3>
            </div>
            <div className="dgb-delete-body">
              <p>
                Are you sure you want to delete this ks
                {(() => {
                  // Extract KS ID from the URL
                  if (icon?.action?.url) {
                    const match = icon.action.url.match(/ksid=(\d+)/);
                    if (match && match[1]) {
                      return ` (ID: ${match[1]})`;
                    }
                  }
                  return '';
                })()}
                ? This action cannot be undone.
              </p>
            </div>
            <div className="dgb-delete-actions">
              <button
                className="dgb-delete-cancel"
                onClick={handleDeleteCancel}
                disabled={isLoading}
              >
                Cancel
              </button>
              <button
                className="dgb-delete-confirm"
                onClick={handleDelete}
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <LucideIcons.Loader2 size={16} className="animate-spin" />
                    Deleting...
                  </>
                ) : (
                  'Delete'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

interface IconTextProps {
  icon: IconConfig;
  content: string | number;
}

const IconText: React.FC<IconTextProps> = ({ icon, content }) => {
  const text = String(content ?? '');
  const shouldWrap = text.length > 15;
  return (
    <div className="dgb-icontext">
      {renderIcon(icon)}
      <div
        className={`dgb-icontext-text ${shouldWrap ? 'dgb-wrap' : 'dgb-nowrap'}`}
      >
        {text}
      </div>
    </div>
  );
};

interface DividerProps {
  orientation?: 'horizontal' | 'vertical';
  color?: string;
  thickness?: number;
}

const Divider: React.FC<DividerProps> = ({
  orientation = 'horizontal',
  color = '#e5e7eb',
  thickness = 1,
}) =>
  orientation === 'vertical' ? (
    <div
      className="dgb-divider-vertical"
      style={{ width: thickness, backgroundColor: color }}
    />
  ) : (
    <div
      className="dgb-divider-horizontal"
      style={{ height: thickness, backgroundColor: color }}
    />
  );

interface SpacerProps {
  size?: number;
}

const Spacer: React.FC<SpacerProps> = ({ size = 12 }) => (
  <div className="dgb-spacer" style={{ height: size }} />
);

const renderCellContent = (
  content: CellContent | undefined,
  cellIcon?: IconWithAction
): JSX.Element | null => {
  if (!content) return null;
  const kind = (content.kind || '').toLowerCase();

  switch (kind) {
    case 'label_value':
      const labelValueContent = content as LabelValueContent;
      // Special handling for "Generated at" label
      if (labelValueContent.label === 'Generated at') {
        // Handle Unix timestamp for "Generated at" label
        const formatTimestamp = (value: string | number): string => {
          // Check if value is a Unix timestamp (number or numeric string)
          const timestamp = Number(value);
          if (!isNaN(timestamp) && timestamp > 0) {
            // Convert Unix timestamp (seconds) to milliseconds and create Date
            const date = new Date(timestamp * 1000);

            // Format options for local machine time
            const options: Intl.DateTimeFormatOptions = {
              year: 'numeric',
              month: 'short',
              day: 'numeric',
              hour: 'numeric',
              minute: '2-digit',
              second: '2-digit',
              hour12: true,
            };

            // Convert to local machine time using browser's timezone
            return date.toLocaleString(navigator.language || 'en-US', options);
          }
          // Fallback to original conversion for other date formats
          return convertDateIntoLocalDate(String(value));
        };

        return (
          <div className="dgb-generated-at">
            <div className="dgb-label">{labelValueContent.label}</div>
            <div className="dgb-value dgb-generated-timestamp">
              {formatTimestamp(labelValueContent.value || '')}
            </div>
          </div>
        );
      }
      return (
        <LabelValue
          label={labelValueContent.label}
          value={labelValueContent.value || ''}
          icon={cellIcon}
          isLink={labelValueContent.is_link}
        />
      );
    case 'icon_text':
      const iconTextContent = content as IconTextContent;
      return (
        <IconText
          icon={iconTextContent.icon}
          content={iconTextContent.content}
        />
      );
    case 'divider':
      const dividerContent = content as DividerContent;
      return (
        <Divider
          orientation={dividerContent.orientation}
          color={dividerContent.color}
          thickness={dividerContent.thickness}
        />
      );
    case 'spacer':
      const spacerContent = content as SpacerContent;
      // spacer_block uses { kind: 'spacer', size: { default: <int> } } in our DS
      const px = spacerContent?.size?.default ?? spacerContent?.height ?? 12;
      return <Spacer size={px} />;
    case 'custom_component': {
      const customContent = content as CustomComponentContent;
      // Known custom blocks handled here for now
      const name = (customContent.component_name || '').toLowerCase();
      const data = customContent.data || {};
      if (name === 'response_tags') {
        // Accepts { tags: [...] }
        return (
          <div className="dgb-section">
            <div className="label dgb-section-label">Tags</div>
            <TagsHandler
              tags={data.tags || []}
              variant="default"
              size="medium"
            />
          </div>
        );
      }
      if (name === 'response_assessment') {
        // Accepts { assessment: [...] }
        return (
          <div className="dgb-section">
            <div className="label dgb-section-label">Assessments</div>
            <Assesment
              assessment={data.assessment || []}
              is_first_three={false}
            />
          </div>
        );
      }
      if (name === 'channel_actions' || name === 'ksc_actions') {
        // Accepts { buttons: [ButtonBlock-like objects] }
        const buttonsBlock = {
          kind: 'button_list',
          buttons: data.buttons || [],
          layout: 'right',
        };
        return <ButtonList block={buttonsBlock} cardContext={undefined} />;
      }
      return null;
    }
    default:
      // Fallback: best-effort render common shapes
      const fallbackContent = content as any; // Type assertion for fallback handling
      if (fallbackContent.label || fallbackContent.value) {
        return (
          <LabelValue
            label={fallbackContent.label}
            value={fallbackContent.value || ''}
            icon={cellIcon}
            isLink={fallbackContent.is_link}
          />
        );
      }
      if (fallbackContent.content) {
        return (
          <div className="dgb-fallback-text">
            {String(fallbackContent.content)}
          </div>
        );
      }
      return null;
  }
};

const DetailsGridBlock: React.FC<DetailsGridBlockProps> = ({ block }) => {
  if (!block) return null;
  const columns = Number(block.columns || 12);
  const gap = block.gap || '12px';
  const rows = Array.isArray(block.rows) ? block.rows : [];

  return (
    <div className="details-grid-block">
      {rows.map((row, rowIdx) => (
        <Row
          key={rowIdx}
          style={
            {
              '--bs-gutter-x': gap,
              '--bs-gutter-y': gap,
            } as React.CSSProperties
          }
        >
          {(row?.cells || []).map((cell, cellIdx) => {
            const rawSpan = Number(cell?.col_span || 1);
            const colSpan = Math.max(1, Math.min(12, rawSpan));
            return (
              <Col
                key={`${rowIdx}-${cellIdx}`}
                xs={colSpan}
                className="details-grid-cell"
              >
                {renderCellContent(cell?.content, cell?.icon)}
              </Col>
            );
          })}
        </Row>
      ))}
    </div>
  );
};

export default DetailsGridBlock;
